import React, { memo, useCallback, useEffect, useState, useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import getLocationMasterColumns, { LOCATION_MASTER_SORT_EXCLUDED_COLUMN_INDEXES } from "./locationMasterColumns";
import Table from "../../ui-components/table/table";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import Button, { ButtonThemes, ButtonSizes } from "../../ui-components/button/button";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledTextInput from "../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import AddEditLocation from "./addEditLocation";
import ConfirmationModal from "../../ui-components/confirmation-modal/confirmationModal";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDoorOpen, faTruck, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import LocationMasterService from "../../app/data/door-master/locationMasterService";
import { LocationMasterDto, LocationType, LocationLifecycleState, LocationMasterRequest, LocationSubType, LocationStatus, LocationUsage } from "../../app/data/door-master/models";
import { ERROR_MESSAGES } from "../../app/data/common/errorMessages";
import { getTerminalsList, terminalSummarySelector } from "../../slices/terminal-summary/terminalSummarySlice";
import TerminalSummaryState from "../../slices/terminal-summary/TerminalSummaryState";
import { ReactComponent as TerminalActivityIcon } from "../../images/TerminalAtivityLog.svg";
import LocationActivityLog from "./locationActivityLog";
import "./locationMaster.scss";

const LOCATION_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "DOOR", value: "DOOR" },
  { label: "STAGING", value: "STAGING" },
  { label: "BIN", value: "BIN" },
  { label: "RACK", value: "RACK" },
  { label: "TEMPORARY", value: "TEMPORARY" },
];

const SUB_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "Loading Door", value: "LOADING_DOOR" },
  { label: "Unloading Door", value: "UNLOADING_DOOR" },
  { label: "City Delivery Door", value: "CITY_DELIVERY_DOOR" },
  { label: "Storage Bin", value: "STORAGE_BIN" },
  { label: "Staging Area", value: "STAGING_AREA" },
  { label: "Temporary Area", value: "TEMPORARY_AREA" },
];

const STATE_OPTIONS: XGSSelectOption[] = [
  { label: "Assigned", value: "ASSIGNED" },
  { label: "Loading", value: "LOADING" },
  { label: "Unloading", value: "UNLOADING" },
  { label: "Closed", value: "CLOSED" },
  { label: "Empty", value: "EMPTY" },
  { label: "Blocked", value: "BLOCKED" },
];

const LocationMaster: React.FC = memo(() => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const locationMasterService = LocationMasterService.getInstance();

  const [terminalFilter, setTerminalFilter] = useState<XGSSelectOption | null>(null);
  const [locationTypeFilter, setLocationTypeFilter] = useState<XGSSelectOption | null>(null);
  const [subTypeFilter, setSubTypeFilter] = useState<XGSSelectOption | null>(null);
  const [stateFilter, setStateFilter] = useState<XGSSelectOption | null>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [deletingLocation, setDeletingLocation] = useState<any>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pageSize] = useState(50);
  const [showLocationActivityLog, setShowLocationActivityLog] = useState(false);

  // Get terminal number from filter only
  const terminalNumber = useMemo(() => {
    if (terminalFilter?.value && typeof terminalFilter.value === "string") {
      return parseInt(terminalFilter.value, 10);
    }
    return null;
  }, [terminalFilter]);

  // Get terminals list for dropdown
  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
  }, [dispatch]);

  const terminalOptions = useMemo(() => {
    if (terminalSummaryState.terminallist) {
      return terminalSummaryState.terminallist.map((terminal) => ({
        label: terminal.terminalName,
        value: terminal.terminalNumber.toString(),
      }));
    }
    return [];
  }, [terminalSummaryState.terminallist]);

  // Get locations from API with infinite scroll
  const {
    data: locationsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isPreviousData,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "locations",
      terminalNumber,
      locationTypeFilter?.value,
      subTypeFilter?.value,
      stateFilter?.value,
      searchString,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      return await locationMasterService.getLocationList({
        terminalNumber: terminalNumber || undefined,
        locationTypeFilter: locationTypeFilter?.value as LocationType | undefined,
        subTypeFilter: subTypeFilter?.value as LocationSubType | undefined,
        lifecycleStateFilter: stateFilter?.value as LocationLifecycleState | undefined,
        search: searchString || undefined,
        page: pageParam,
        size: pageSize,
        sort: "locationCode,asc",
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.last) return undefined;
      return lastPage.pageable.pageNumber + 1;
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Get summary counts
  const { data: summaryCounts } = useQuery({
    queryKey: ["locationSummaryCounts", terminalNumber],
    queryFn: async () => {
      return await locationMasterService.getSummaryCounts(terminalNumber || undefined);
    },
    refetchOnWindowFocus: false,
  });

  // Transform API response to match table format - flatten all pages
  const locations = useMemo(() => {
    if (!locationsData?.pages) {
      return [];
    }

    const allLocations = locationsData.pages.flatMap((page) => page.content);

    return allLocations.map((loc: LocationMasterDto) => ({
      ...loc,
      // Override with table-specific mappings
      subType: loc.subType,
      trailer: loc.trailerNumber,
      distributorConsignee: loc.distributorName,
      terminalNumber: loc.terminalNumber,
      manifestNumber: loc.manifestNumber,
      percentFilled: loc.percentFilled || 0,
      totalWeight: loc.totalWeight || 0,
      distributorName: loc.distributorName,
      state: loc.currentLifecycleState || loc.status,
    }));
  }, [locationsData]);


  const filteredLocations = locations; // API handles filtering, so no client-side filtering needed

  const onSearchStart = useCallback(() => {
    refetch();
  }, [refetch]);

  const clear = () => {
    setTerminalFilter(null);
    setLocationTypeFilter(null);
    setSubTypeFilter(null);
    setStateFilter(null);
    setSearchString("");
  };

  // Debounce search - refetch when search string changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (terminalNumber) {
        refetch();
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchString, terminalFilter, refetch, terminalNumber]);

  const baseFilters = (
    <div className="xgs-location-master-summary__controls">
      <div className="xgs-location-master-summary__bottom-row">
        <div className="xgs-location-master-summary__form xgs-location-master-summary__item">
          <div className="xgs-location-master-summary__controls__double-item">
            <LabeledTextInput
              name="searchString"
              labelMode={LabelModes.column}
              className="xgs-location-master-summary__input"
              value={searchString}
              onChange={(e) => setSearchString(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSearchStart();
                }
              }}
              type="text"
              placeholder="Search by location code, distributor, trailer, carrier..."
            />
          </div>
          <div className="xgs-location-master-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={(option) => setTerminalFilter(option || null)}
              name="terminal"
              value={terminalFilter}
              options={terminalOptions}
              labelMode={LabelModes.column}
              className="xgs-location-master-summary__input"
              placeholder="All Terminals"
              isClearable
            />
          </div>
          <div className="xgs-location-master-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={(option) => {
                setLocationTypeFilter(option || null);
                setSubTypeFilter(null); // Reset sub-type when location type changes
              }}
              name="locationType"
              value={locationTypeFilter}
              options={LOCATION_TYPE_OPTIONS}
              labelMode={LabelModes.column}
              className="xgs-location-master-summary__input"
              placeholder="All Types"
              isClearable
            />
          </div>
          <div className="xgs-location-master-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={(option) => setSubTypeFilter(option || null)}
              name="subType"
              value={subTypeFilter}
              options={SUB_TYPE_OPTIONS}
              labelMode={LabelModes.column}
              className="xgs-location-master-summary__input"
              placeholder="All Sub Types"
              disabled={!locationTypeFilter}
              isClearable
            />
          </div>
          <div className="xgs-location-master-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={(option) => setStateFilter(option || null)}
              name="state"
              value={stateFilter}
              options={STATE_OPTIONS}
              labelMode={LabelModes.column}
              className="xgs-location-master-summary__input"
              placeholder="All States"
              isClearable
            />
          </div>
          <Button
            theme={ButtonThemes.gray}
            onClick={clear}
            disabled={!terminalFilter && !locationTypeFilter && !subTypeFilter && !stateFilter && !searchString}
            type="button"
          >
            Clear Filters
          </Button>
          <Button theme={ButtonThemes.blue} onClick={onSearchStart} spinner={isLoading}>
            Search
          </Button>
        </div>
        <Button
          theme={ButtonThemes.blue}
          onClick={() => {
            setEditingLocation(null);
            setSidebarOpen(true);
          }}
        >
          + Add Location
        </Button>
      </div>
    </div>
  );

  const handleEdit = (location: any) => {
    setEditingLocation(location);
    setSidebarOpen(true);
  };

  const handleDelete = (location: any) => {
    setDeletingLocation(location);
    setShowDeleteConfirmation(true);
  };

  // Create location mutation
  const createLocationMutation = useMutation({
    mutationFn: (data: LocationMasterRequest) => {
      return locationMasterService.createLocation(data);
    },
    onSuccess: () => {
      toast.success("Location created successfully");
      setSidebarOpen(false);
      setEditingLocation(null);
      // Invalidate location list queries
      queryClient.invalidateQueries({
        queryKey: ["locations"],
      });
      // Invalidate summary counts
      queryClient.invalidateQueries({
        queryKey: ["locationSummaryCounts"],
      });
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });

  // Update location mutation
  const updateLocationMutation = useMutation({
    mutationFn: (data: LocationMasterRequest) => {
      if (!editingLocation?.id) {
        return Promise.reject(new Error("Location ID is required"));
      }
      return locationMasterService.updateLocation(editingLocation.id, data);
    },
    onSuccess: () => {
      toast.success("Location updated successfully");
      setSidebarOpen(false);
      setEditingLocation(null);
      // Invalidate location list queries
      queryClient.invalidateQueries({
        queryKey: ["locations"],
      });
      // Invalidate summary counts
      queryClient.invalidateQueries({
        queryKey: ["locationSummaryCounts"],
      });
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });

  // Delete location mutation
  const deleteLocationMutation = useMutation({
    mutationFn: (id: string) => {
      return locationMasterService.deleteLocation(id);
    },
    onSuccess: () => {
      toast.success("Location deleted successfully");
      setShowDeleteConfirmation(false);
      setDeletingLocation(null);
      // Invalidate location list queries
      queryClient.invalidateQueries({
        queryKey: ["locations"],
      });
      // Invalidate summary counts
      queryClient.invalidateQueries({
        queryKey: ["locationSummaryCounts"],
      });
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      } else if (typeof e?.message === "string") {
        toast.error(e.message);
      } else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });

  const handleSave = (values: any) => {
    // Transform form values to API request format
    const request: LocationMasterRequest = {
      locationCode: values.locationCode,
      locationType: values.locationType as LocationType,
      subType: values.subType ? (values.subType as LocationSubType) : undefined,
      description: values.description || undefined,
      isActive: values.isActive === "true" || values.isActive === true,
      distributorNumber: values.distributorNumber || undefined,
      distributorName: values.distributorName || undefined,
      priority: values.priority || undefined,
      status: values.status ? (values.status as LocationStatus) : undefined,
      usage: values.usage ? (values.usage as LocationUsage) : undefined,
      trailerNumber: values.trailer || undefined,
      carrierNumber: values.carrier || undefined,
      terminalNumber: values.terminalNumber && values.terminalNumber.trim() !== "" 
        ? parseInt(values.terminalNumber, 10) 
        : undefined,
      // Include secondary distributors - send the complete list
      secondaryDistributors: values.secondaryDistributors || [],
    };

    if (editingLocation?.id) {
      updateLocationMutation.mutate(request);
      
    } else {
      createLocationMutation.mutate(request);
    }
  };

  return (
    <ContentContainer
      titleComponent={
        <ContentContainerToolbar pageId={PAGE_IDS.TERMINAL_SUMMARY} title="Location Master" className="terminal-summary">
          <div className="terminal-summary__terminal-activitylog">
            <Button
              theme={ButtonThemes.transparent}
              size={ButtonSizes.auto}
              onClick={() => setShowLocationActivityLog(true)}
            >
              <div className="xgs-btn__icon-container">
                <TerminalActivityIcon className="xgs-btn__icon" />
              </div>
              Location Activity Log
            </Button>
          </div>
        </ContentContainerToolbar>
      }
    >
      <>
        {baseFilters}
        {summaryCounts && (
          <div className="xgs-location-master-summary__summary">
            <div className="xgs-location-master-summary">
              <div className="xgs-location-master-summary__tile">
                <div className="icon-container --type-totalLocations">
                  <FontAwesomeIcon icon={faDoorOpen} style={{ fontSize: "20px", color: "#2F80ED" }} />
                </div>
                <div className="content">
                  <div className="info_location_master">
                    <div className="value">{summaryCounts.activeLocations}</div>
                  </div>
                  <div className="info_location_master">
                    <div className="title">Total Locations</div>
                  </div>
                </div>
              </div>
              <div className="xgs-location-master-summary__tile">
                <div className="icon-container --type-inUse">
                  <FontAwesomeIcon icon={faTruck} style={{ fontSize: "20px", color: "#FF9800" }} />
                </div>
                <div className="content">
                  <div className="info_location_master">
                    <div className="value">{summaryCounts.inUse}</div>
                  </div>
                  <div className="info_location_master">
                    <div className="title">In Use</div>
                  </div>
                </div>
              </div>
              <div className="xgs-location-master-summary__tile">
                <div className="icon-container --type-active">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: "20px", color: "#9747FF" }} />
                </div>
                <div className="content">
                  <div className="info_location_master">
                    <div className="value">{summaryCounts.available}</div>
                  </div>
                  <div className="info_location_master">
                    <div className="title">Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="xgs-location-master-summary__orders">
          <Table
            columns={getLocationMasterColumns(handleEdit, handleDelete)}
            data={filteredLocations}
            noResultsText="There are no locations available"
            responsive={true}
            sorting={true}
            filters={true}
            minTableHeight={420}
            sortingExcludeColumnIndexes={LOCATION_MASTER_SORT_EXCLUDED_COLUMN_INDEXES}
            cursorPointer={false}
            autoResetSelectedRows={true}
            infiniteScroll={true}
            isLoading={isLoading || (isFetching && isPreviousData)}
            keepTableOnLoading
            infiniteScrollHasNext={hasNextPage}
            infiniteScrollLoading={isFetchingNextPage}
            onInfiniteScroll={fetchNextPage}
          />
        </div>
      </>
      <AddEditLocation
        show={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setEditingLocation(null);
        }}
        onSave={handleSave}
        location={editingLocation}
        isMutating={createLocationMutation.isLoading || updateLocationMutation.isLoading}
      />
      <ConfirmationModal
        opened={showDeleteConfirmation}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setDeletingLocation(null);
        }}
        onConfirm={() => {
          if (deletingLocation?.id) {
            deleteLocationMutation.mutate(deletingLocation.id);
          }
        }}
        spinner={deleteLocationMutation.isLoading}
        header="Delete Location"
        confirmButtonText="Delete"
      >
        <div className="xgs-location-master-summary__modal-content">
          <p>
            Are you sure you want to delete the location:<br />
            <strong>{deletingLocation?.locationCode}</strong>?
          </p>
          <p style={{ marginTop: "10px", color: "#666" }}>
            This action cannot be undone.
          </p>
        </div>
      </ConfirmationModal>
      <LocationActivityLog
        show={showLocationActivityLog}
        onClose={() => setShowLocationActivityLog(false)}
      />
    </ContentContainer>
  );
});

export default LocationMaster;
